import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, push, set, update, remove } from 'firebase/database';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ReportAnalysis.css';

const PaymentEntry = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [paymentMode, setPaymentMode] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [viewingPayment, setViewingPayment] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([{
    id: Date.now(),
    date: new Date().toISOString().split('T')[0],
    partyName: '',
    partyContact: '',
    expenseType: '',
    employeeId: '',
    paymentMode: '',
    paymentProof: '',
    utrNo: '',
    amount: '',
    referenceNo: '',
    description: ''
  }]);
  const [submitting, setSubmitting] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(0);

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format expense type for display
  const formatExpenseType = (type) => {
    if (!type) return 'N/A';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Compress image before upload to approximately 50KB
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const TARGET_SIZE_KB = 50;
          const TARGET_SIZE_BYTES = TARGET_SIZE_KB * 1024;

          let MAX_WIDTH = 800;
          let MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const compressWithQuality = (quality) => {
            return new Promise((resolveBlob) => {
              canvas.toBlob(
                (blob) => {
                  const reader = new FileReader();
                  reader.readAsDataURL(blob);
                  reader.onloadend = () => {
                    resolveBlob({
                      dataUrl: reader.result,
                      size: blob.size
                    });
                  };
                },
                'image/jpeg',
                quality
              );
            });
          };

          const tryCompress = async () => {
            let quality = 0.7;
            let result = await compressWithQuality(quality);

            if (result.size > TARGET_SIZE_BYTES) {
              quality = 0.5;
              result = await compressWithQuality(quality);

              if (result.size > TARGET_SIZE_BYTES) {
                quality = 0.3;
                result = await compressWithQuality(quality);

                if (result.size > TARGET_SIZE_BYTES) {
                  quality = 0.2;
                  result = await compressWithQuality(quality);
                }
              }
            }

            resolve(result.dataUrl);
          };

          tryCompress();
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  useEffect(() => {
    fetchPayments();
    fetchEmployees();
  }, []);

  const fetchPayments = async () => {
    try {
      const db = getDatabase();
      const paymentsRef = ref(db, 'HTAMS/expenses/paymententry');
      const snapshot = await get(paymentsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const paymentsList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setPayments(paymentsList.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } else {
        setPayments([]);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const db = getDatabase();
      const employeesRef = ref(db, 'HTAMS/employees');
      const snapshot = await get(employeesRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const employeesList = Object.keys(data).map(key => ({
          id: key,
          name: data[key].name,
          email: data[key].email,
          mobile: data[key].mobile
        }));
        setEmployees(employeesList);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      try {
        toast.info('Compressing image...');
        const compressedImage = await compressImage(file);
        setEntries(prev => prev.map((entry, i) =>
          i === index ? { ...entry, paymentProof: compressedImage } : entry
        ));
        toast.success('Image uploaded successfully');
      } catch (error) {
        console.error('Error compressing image:', error);
        toast.error('Failed to compress image');
      }
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;

    // Validation for party contact
    if (name === 'partyContact') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setEntries(prev => prev.map((entry, i) =>
          i === index ? { ...entry, [name]: numericValue } : entry
        ));
      }
      return;
    }

    // Validation for amount
    if (name === 'amount') {
      const numericValue = value.replace(/[^\d.]/g, '');
      const parts = numericValue.split('.');
      const validValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
      setEntries(prev => prev.map((entry, i) =>
        i === index ? { ...entry, [name]: validValue } : entry
      ));
      return;
    }

    // Validation for reference number (alphanumeric)
    if (name === 'referenceNo') {
      const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setEntries(prev => prev.map((entry, i) =>
        i === index ? { ...entry, [name]: alphanumericValue } : entry
      ));
      return;
    }

    // Validation for UTR number (alphanumeric)
    if (name === 'utrNo') {
      const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setEntries(prev => prev.map((entry, i) =>
        i === index ? { ...entry, [name]: alphanumericValue } : entry
      ));
      return;
    }

    // Handle employee selection
    if (name === 'employeeId' && value) {
      const selectedEmployee = employees.find(emp => emp.id === value);
      if (selectedEmployee) {
        setEntries(prev => prev.map((entry, i) =>
          i === index ? {
            ...entry,
            employeeId: value,
            partyName: selectedEmployee.name,
            partyContact: selectedEmployee.mobile || ''
          } : entry
        ));
        return;
      }
    }

    setEntries(prev => prev.map((entry, i) =>
      i === index ? { ...entry, [name]: value } : entry
    ));
  };

  const addNewEntry = () => {
    const newIndex = entries.length;
    setEntries(prev => [...prev, {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      partyName: '',
      partyContact: '',
      expenseType: '',
      employeeId: '',
      paymentMode: '',
      paymentProof: '',
      utrNo: '',
      amount: '',
      referenceNo: '',
      description: ''
    }]);
    setExpandedEntry(newIndex);
  };

  const removeEntry = (index) => {
    if (entries.length > 1) {
      setEntries(prev => prev.filter((_, i) => i !== index));
      setExpandedEntry(index > 0 ? index - 1 : 0);
    } else {
      toast.warning('At least one entry is required');
    }
  };

  const toggleEntry = (index) => {
    setExpandedEntry(expandedEntry === index ? index : index);
  };

  const validateEntry = (entry, index) => {
    const entryNum = index + 1;

    // Date validation
    if (!entry.date) {
      toast.error(`Entry ${entryNum}: Date is required`);
      return false;
    }

    // Expense type validation
    if (!entry.expenseType) {
      toast.error(`Entry ${entryNum}: Expense type is required`);
      return false;
    }

    // If salary expense type, validate employee selection
    if (entry.expenseType === 'salary' && !entry.employeeId) {
      toast.error(`Entry ${entryNum}: Please select an employee for salary expense`);
      return false;
    }

    // Party name validation
    if (!entry.partyName || entry.partyName.trim() === '') {
      toast.error(`Entry ${entryNum}: Party name is required`);
      return false;
    }

    // Party contact validation
    if (entry.partyContact && entry.partyContact.trim()) {
      if (!/^\d{10}$/.test(entry.partyContact.trim())) {
        toast.error(`Entry ${entryNum}: Party contact must be 10 digits`);
        return false;
      }
    }

    // Amount validation
    if (!entry.amount || entry.amount.trim() === '') {
      toast.error(`Entry ${entryNum}: Amount is required`);
      return false;
    }

    if (parseFloat(entry.amount) <= 0) {
      toast.error(`Entry ${entryNum}: Amount must be greater than 0`);
      return false;
    }

    // Payment mode validation
    if (!entry.paymentMode) {
      toast.error(`Entry ${entryNum}: Payment mode is required`);
      return false;
    }

    // Payment proof validation for cash and online
    if ((entry.paymentMode === 'cash' || entry.paymentMode === 'online') && !entry.paymentProof) {
      toast.error(`Entry ${entryNum}: Payment proof image is required for ${entry.paymentMode} payment`);
      return false;
    }

    // UTR No validation for cheque
    if (entry.paymentMode === 'cheque' && (!entry.utrNo || entry.utrNo.trim() === '')) {
      toast.error(`Entry ${entryNum}: UTR No / Cheque No is required for cheque payment`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation for all entries
    for (let i = 0; i < entries.length; i++) {
      if (!validateEntry(entries[i], i)) {
        setExpandedEntry(i); // Expand the entry with validation error
        return;
      }
    }

    setSubmitting(true);
    try {
      const db = getDatabase();

      if (editingId) {
        // Update existing payment (single entry)
        const entry = entries[0];
        const paymentData = {
          date: entry.date,
          partyName: entry.partyName,
          partyContact: entry.partyContact,
          expenseType: entry.expenseType,
          employeeId: entry.employeeId,
          paymentMode: entry.paymentMode,
          paymentProof: entry.paymentProof,
          utrNo: entry.utrNo,
          amount: parseFloat(entry.amount),
          referenceNo: entry.referenceNo,
          description: entry.description,
          updatedAt: new Date().toISOString(),
          timestamp: Date.now()
        };

        const paymentRef = ref(db, `HTAMS/expenses/paymententry/${editingId}`);
        await update(paymentRef, paymentData);
        toast.success('Payment entry updated successfully!');
        setEditingId(null);
      } else {
        // Create new payments (multiple entries)
        let successCount = 0;

        for (const entry of entries) {
          const paymentData = {
            date: entry.date,
            partyName: entry.partyName,
            partyContact: entry.partyContact,
            expenseType: entry.expenseType,
            employeeId: entry.employeeId,
            paymentMode: entry.paymentMode,
            paymentProof: entry.paymentProof,
            utrNo: entry.utrNo,
            amount: parseFloat(entry.amount),
            referenceNo: entry.referenceNo,
            description: entry.description,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            timestamp: Date.now()
          };

          const paymentsRef = ref(db, 'HTAMS/expenses/paymententry');
          const newPaymentRef = push(paymentsRef);
          await set(newPaymentRef, paymentData);
          successCount++;
        }

        toast.success(`${successCount} payment ${successCount > 1 ? 'entries' : 'entry'} added successfully!`);
      }

      setShowForm(false);
      setEntries([{
        id: Date.now(),
        date: new Date().toISOString().split('T')[0],
        partyName: '',
        partyContact: '',
        expenseType: '',
        employeeId: '',
        paymentMode: '',
        paymentProof: '',
        utrNo: '',
        amount: '',
        referenceNo: '',
        description: ''
      }]);

      fetchPayments();
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error('Failed to save payment entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (payment) => {
    setEditingId(payment.id);
    setEntries([{
      id: payment.id,
      date: payment.date,
      partyName: payment.partyName,
      partyContact: payment.partyContact || '',
      expenseType: payment.expenseType || '',
      employeeId: payment.employeeId || '',
      paymentMode: payment.paymentMode || '',
      paymentProof: payment.paymentProof || '',
      utrNo: payment.utrNo || '',
      amount: payment.amount.toString(),
      referenceNo: payment.referenceNo || '',
      description: payment.description || ''
    }]);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, partyName) => {
    if (window.confirm(`Are you sure you want to delete payment entry for "${partyName}"?`)) {
      try {
        const db = getDatabase();
        const paymentRef = ref(db, `HTAMS/expenses/paymententry/${id}`);
        await remove(paymentRef);
        toast.success('Payment entry deleted successfully!');
        fetchPayments();
      } catch (error) {
        console.error('Error deleting payment:', error);
        toast.error('Failed to delete payment entry');
      }
    }
  };

  const handleView = (payment) => {
    setViewingPayment(payment);
  };

  const closeViewModal = () => {
    setViewingPayment(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowForm(false);
    setEntries([{
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      partyName: '',
      partyContact: '',
      expenseType: '',
      employeeId: '',
      paymentMode: '',
      paymentProof: '',
      utrNo: '',
      amount: '',
      referenceNo: '',
      description: ''
    }]);
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.partyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.referenceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.expenseType?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = (!dateFilter.start || new Date(payment.date) >= new Date(dateFilter.start)) &&
      (!dateFilter.end || new Date(payment.date) <= new Date(dateFilter.end));
    const matchesMode = paymentMode === 'all' || payment.paymentMode === paymentMode;
    return matchesSearch && matchesDate && matchesMode;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, paymentMode]);

  const exportToExcel = () => {
    if (filteredPayments.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const exportData = filteredPayments.map((payment, index) => ({
      'Sr No': index + 1,
      'Date': formatDate(payment.date),
      'Party Name': payment.partyName || '-',
      'Party Contact': payment.partyContact || '-',
      'Expense Type': formatExpenseType(payment.expenseType),
      'Payment Mode': (payment.paymentMode || '-').toUpperCase(),
      'Amount': payment.amount,
      'Reference No': payment.referenceNo || '-',
      'Description': payment.description || '-'
    }));

    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row =>
        headers.map(header => {
          const value = row[header];
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Payment_Entries_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredPayments.length} entries successfully!`);
  };

  if (loading) return <div className="loading">Loading payments...</div>;

  return (
    <div className="report-analysis-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="page-header">
        <h2>Payment Entry</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="add-btn"
            onClick={exportToExcel}
            style={{ background: '#28a745' }}
          >
            📥 Export to Excel
          </button>
          <button
            className="add-btn"
            onClick={() => {
              if (showForm && editingId) {
                handleCancelEdit();
              } else {
                setShowForm(!showForm);
              }
            }}
          >
            {showForm ? '✕ Close' : '+ Add Payment'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-container">
          <h3 className="form-title">{editingId ? 'Edit Payment Entry' : 'Add New Payment Entry'}</h3>
          <form onSubmit={handleSubmit} className="purchase-form">
            {entries.map((entry, index) => {
              const isExpanded = expandedEntry === index;
              return (
                <div
                  key={entry.id}
                  style={{
                    marginBottom: '10px',
                    border: isExpanded ? '2px solid #2c5f2d' : '2px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div
                    onClick={() => toggleEntry(index)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 15px',
                      background: isExpanded ? '#2c5f2d' : '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <h4 style={{ margin: 0, color: isExpanded ? 'white' : '#2c5f2d', fontWeight: 'bold' }}>
                        Entry {index + 1}
                      </h4>
                      {entry.partyName && (
                        <span style={{ fontSize: '14px', color: isExpanded ? '#d4edda' : '#666' }}>
                          {entry.partyName}
                        </span>
                      )}
                      {entry.amount && (
                        <span style={{ fontSize: '14px', color: isExpanded ? '#d4edda' : '#666', fontWeight: 'bold' }}>
                          ₹{entry.amount}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {!editingId && entries.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeEntry(index);
                          }}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '5px 15px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Remove
                        </button>
                      )}
                      <span style={{ color: isExpanded ? 'white' : '#2c5f2d', fontSize: '20px', fontWeight: 'bold' }}>
                        {isExpanded ? '−' : '+'}
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '15px' }}>
                      <div className="form-grid" style={{ gap: '12px' }}>
                        <div className="form-group">
                          <label>Date <span className="required">*</span></label>
                          <input
                            type="date"
                            name="date"
                            value={entry.date}
                            onChange={(e) => handleInputChange(e, index)}
                            required
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label>Expense Type <span className="required">*</span></label>
                          <select
                            name="expenseType"
                            value={entry.expenseType}
                            onChange={(e) => handleInputChange(e, index)}
                            required
                            className="form-control"
                          >
                            <option value="">Select</option>
                            <option value="office_expenses">Office Expenses</option>
                            <option value="travel">Travel</option>
                            <option value="salary">Salary</option>
                            <option value="utilities">Utilities</option>
                            <option value="rent">Rent</option>
                            <option value="marketing">Marketing</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        {entry.expenseType === 'salary' && (
                          <div className="form-group">
                            <label>Employee <span className="required">*</span></label>
                            <select
                              name="employeeId"
                              value={entry.employeeId}
                              onChange={(e) => handleInputChange(e, index)}
                              required
                              className="form-control"
                            >
                              <option value="">Select Employee</option>
                              {employees.map(emp => (
                                <option key={emp.id} value={emp.id}>
                                  {emp.name} - {emp.email}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div className="form-group">
                          <label>Party Name <span className="required">*</span></label>
                          <input
                            type="text"
                            name="partyName"
                            value={entry.partyName}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter party name"
                            required
                            className="form-control"
                            readOnly={entry.expenseType === 'salary' && entry.employeeId}
                          />
                        </div>

                        <div className="form-group">
                          <label>Party Contact</label>
                          <input
                            type="text"
                            name="partyContact"
                            value={entry.partyContact}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter contact number (10 digits)"
                            className="form-control"
                            readOnly={entry.expenseType === 'salary' && entry.employeeId}
                          />
                        </div>

                        <div className="form-group">
                          <label>Payment Mode <span className="required">*</span></label>
                          <select
                            name="paymentMode"
                            value={entry.paymentMode}
                            onChange={(e) => handleInputChange(e, index)}
                            required
                            className="form-control"
                          >
                            <option value="">Select</option>
                            <option value="cash">Cash</option>
                            <option value="online">Online</option>
                            <option value="cheque">Cheque</option>
                          </select>
                        </div>

                        {/* Conditional fields based on payment mode */}
                        {(entry.paymentMode === 'cash' || entry.paymentMode === 'online') && (
                          <div className="form-group">
                            <label>Payment Proof (Image) <span className="required">*</span></label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, index)}
                              className="form-control"
                            />
                            {entry.paymentProof && (
                              <div className="image-preview" style={{ marginTop: '10px', position: 'relative', display: 'inline-block' }}>
                                <img
                                  src={entry.paymentProof}
                                  alt="Payment Proof"
                                  style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', padding: '5px' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setEntries(prev => prev.map((e, i) => i === index ? { ...e, paymentProof: '' } : e))}
                                  style={{
                                    position: 'absolute',
                                    top: '5px',
                                    right: '5px',
                                    background: '#ff4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '30px',
                                    height: '30px',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    lineHeight: '1',
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                                  }}
                                  title="Remove image"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {entry.paymentMode === 'cheque' && (
                          <div className="form-group">
                            <label>UTR No / Cheque No <span className="required">*</span></label>
                            <input
                              type="text"
                              name="utrNo"
                              value={entry.utrNo}
                              onChange={(e) => handleInputChange(e, index)}
                              placeholder="Enter UTR or Cheque number"
                              required
                              className="form-control"
                            />
                          </div>
                        )}

                        <div className="form-group">
                          <label>Amount <span className="required">*</span></label>
                          <input
                            type="text"
                            name="amount"
                            value={entry.amount}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter amount"
                            required
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label>Reference No</label>
                          <input
                            type="text"
                            name="referenceNo"
                            value={entry.referenceNo}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter reference/transaction number"
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label>Description</label>
                          <textarea
                            name="description"
                            value={entry.description}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter description or notes"
                            rows="3"
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!editingId && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={addNewEntry}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  + Add Another Entry
                </button>
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-submit"
              >
                {submitting ? 'Saving...' : (editingId ? 'Update Payment' : `Save ${entries.length} Payment${entries.length > 1 ? 's' : ''}`)}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters">
        <input
          type="text"
          placeholder="Search by party name, reference, or expense type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ flex: '2' }}
        />

        <select
          value={paymentMode}
          onChange={(e) => setPaymentMode(e.target.value)}
          className="search-input"
          style={{ cursor: 'pointer' }}
        >
          <option value="all">All Payment Modes</option>
          <option value="cash">Cash</option>
          <option value="online">Online</option>
          <option value="cheque">Cheque</option>
        </select>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Start Date</label>
          <input
            type="date"
            value={dateFilter.start}
            onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
            className="date-input"
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>End Date</label>
          <input
            type="date"
            value={dateFilter.end}
            onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
            className="date-input"
          />
        </div>

        {(searchTerm || paymentMode !== 'all' || dateFilter.start || dateFilter.end) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setPaymentMode('all');
              setDateFilter({ start: '', end: '' });
            }}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Date</th>
              <th>Party Name</th>
              <th>Expense Type</th>
              <th>Payment Mode</th>
              <th>Amount</th>
              <th>Reference No</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentPayments.length > 0 ? (
              currentPayments.map((payment, index) => (
                <tr key={payment.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{formatDate(payment.date)}</td>
                  <td>{payment.partyName}</td>
                  <td>{formatExpenseType(payment.expenseType)}</td>
                  <td>{payment.paymentMode}</td>
                  <td>₹{payment.amount?.toLocaleString()}</td>
                  <td>{payment.referenceNo || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view-btn"
                        onClick={() => handleView(payment)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(payment)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(payment.id, payment.partyName)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : null}
          </tbody>
        </table>

        {currentPayments.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">💰</div>
            <h3>No payment entries found</h3>
            <p>Add payment entries using the "Add Payment Entry" button above.</p>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewingPayment && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payment Entry Details</h3>
              <button className="modal-close" onClick={closeViewModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Date:</label>
                  <span>{formatDate(viewingPayment.date)}</span>
                </div>
                <div className="detail-item">
                  <label>Party Name:</label>
                  <span>{viewingPayment.partyName}</span>
                </div>
                <div className="detail-item">
                  <label>Party Contact:</label>
                  <span>{viewingPayment.partyContact || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Expense Type:</label>
                  <span className="expense-badge">{formatExpenseType(viewingPayment.expenseType)}</span>
                </div>
                <div className="detail-item">
                  <label>Payment Mode:</label>
                  <span className="payment-badge">{viewingPayment.paymentMode}</span>
                </div>
                <div className="detail-item">
                  <label>Amount:</label>
                  <span className="highlight-amount">₹{viewingPayment.amount?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Reference No:</label>
                  <span>{viewingPayment.referenceNo || 'N/A'}</span>
                </div>
                {viewingPayment.utrNo && (
                  <div className="detail-item">
                    <label>UTR No / Cheque No:</label>
                    <span>{viewingPayment.utrNo}</span>
                  </div>
                )}
              </div>

              {/* Payment Mode Section */}
              {viewingPayment.paymentMode && (
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#495057',
                    marginBottom: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    PAYMENT MODE:
                  </div>
                  <div style={{
                    display: 'inline-block',
                    padding: '6px 16px',
                    background: '#007bff',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    textTransform: 'uppercase'
                  }}>
                    {viewingPayment.paymentMode}
                  </div>
                </div>
              )}

              {/* Payment Proof Section */}
              {viewingPayment.paymentProof && (
                <div style={{
                  marginTop: '20px',
                  padding: '20px',
                  background: '#e8f5e9',
                  borderRadius: '8px',
                  border: '1px solid #c8e6c9'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#2e7d32',
                    marginBottom: '15px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    PAYMENT PROOF:
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '15px'
                  }}>
                    <img
                      src={viewingPayment.paymentProof}
                      alt="Payment Proof"
                      style={{
                        maxWidth: '400px',
                        width: '100%',
                        height: 'auto',
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        border: '2px solid #fff'
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      gap: '10px'
                    }}>
                      <button
                        onClick={() => window.open(viewingPayment.paymentProof, '_blank')}
                        style={{
                          padding: '10px 20px',
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        👁️ View Full Image
                      </button>
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = viewingPayment.paymentProof;
                          link.download = `payment_proof_${viewingPayment.partyName}_${viewingPayment.date}.jpg`;
                          link.click();
                        }}
                        style={{
                          padding: '10px 20px',
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        ⬇️ Download Image
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Description Section */}
              {viewingPayment.description && (
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: '#fff3cd',
                  borderRadius: '8px',
                  border: '1px solid #ffeaa7'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#856404',
                    marginBottom: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    DESCRIPTION:
                  </div>
                  <div style={{
                    color: '#856404',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {viewingPayment.description}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {filteredPayments.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredPayments.length)} of {filteredPayments.length} entries
          </div>
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <span key={pageNumber} className="pagination-dots">...</span>;
              }
              return null;
            })}

            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentEntry;
